/**
 * Floating dropdown shared by the exam sections (t-exam-cta, t-specs).
 *
 * A toggle button opens a panel below it. The behaviour is local toggle state,
 * so this is a plain custom element with no Shopify event machinery -- the same
 * shape the CTA dropdown has always had.
 *
 * One class, one element name: a class extending HTMLElement can only be
 * registered once per registry, so both sections render `<exam-dropdown>` rather
 * than each having a name of its own.
 *
 * Queries are re-resolved in `connectedCallback` because section re-renders
 * replace the DOM and would leave cached references pointing at detached nodes.
 */

const BUTTON_SELECTOR = '[data-exam-dropdown-button]';
const PANEL_SELECTOR = '[data-exam-dropdown-panel]';

class ExamDropdown extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector(BUTTON_SELECTOR);
    this.panel = this.querySelector(PANEL_SELECTOR);
    if (!this.button || !this.panel) return;

    this.button.addEventListener('click', this.toggle);
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.toggle);
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  get isOpen() {
    return this.button?.getAttribute('aria-expanded') === 'true';
  }

  toggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  open() {
    if (!this.button || !this.panel) return;
    this.panel.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
  }

  /**
   * @param {boolean} [refocus=false] - Return focus to the toggle button.
   */
  close(refocus = false) {
    if (!this.button || !this.panel) return;
    this.panel.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    if (refocus) this.button.focus();
  }

  handleDocumentClick = (event) => {
    if (this.isOpen && !this.contains(event.target)) {
      this.close();
    }
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      this.close(true);
    }
  };
}

if (!customElements.get('exam-dropdown')) {
  customElements.define('exam-dropdown', ExamDropdown);
}